import { Link } from 'react-router';
import { Panel } from '../components/Panel.js';
import {
  Badge,
  Card,
  ErrorNote,
  Icon,
  PageHeader,
  PagedTableShell,
  Stat,
  Table,
  Td,
  buttonClass,
  useClientPaging,
} from '../components/ui/index.js';
import { activityIcon } from '../lib/activity-icon.js';
import { useAdminLocale } from '../lib/admin-locale.js';
import { useRemote } from '../lib/remote.js';

interface SiteRow {
  id: string;
  name: string;
  canonicalOrigin: string;
  status: string;
}

interface UsageRow {
  period: { startsAt: string; endsAt: string; timezone: string };
  answers: { quota: number; completed: number; reserved: number };
  estimatedCost: { amount: string; currency: string; uncertainCalls: number; label: string };
}

interface AuditRow {
  id: string;
  action: string;
  createdAt: string;
}

const AUDIT_LABEL: Record<string, readonly [el: string, en: string]> = {
  'site.created': ['Νέα ιστοσελίδα', 'New website'],
  'site.updated': ['Ενημέρωση ιστοσελίδας', 'Website updated'],
  'platform.site_created': ['Νέα ιστοσελίδα', 'New website'],
  'bot.updated': ['Ενημέρωση bot', 'Bot updated'],
  'origin.added': ['Νέο origin', 'Origin added'],
  'origin.removed': ['Αφαίρεση origin', 'Origin removed'],
  'pairing.issued': ['Κωδικός σύνδεσης', 'Connection code issued'],
  'installation.paired': ['Σύνδεση WordPress', 'WordPress connected'],
  'installation.revoked': ['Ανάκληση σύνδεσης', 'Connection revoked'],
  'installation.disconnected': ['Αποσύνδεση WordPress', 'WordPress disconnected'],
  'installation.rotated': ['Ανανέωση διαπιστευτηρίων', 'Credentials rotated'],
  'sync.requested': ['Αίτημα συγχρονισμού', 'Sync requested'],
  'sync.completed': ['Ολοκλήρωση συγχρονισμού', 'Sync completed'],
  'source.created': ['Νέα πηγή', 'New source'],
  'source.uploaded': ['Μεταφόρτωση πηγής', 'Source uploaded'],
  'source.updated': ['Ενημέρωση πηγής', 'Source updated'],
  'source.activated': ['Ενεργοποίηση πηγής', 'Source activated'],
  'source.rejected': ['Απόρριψη πηγής', 'Source rejected'],
  'source.bulk_activated': ['Μαζική ενεργοποίηση πηγών', 'Sources activated in bulk'],
  'source.bulk_rejected': ['Μαζική απόρριψη πηγών', 'Sources rejected in bulk'],
  'site.deleted': ['Διαγραφή ιστοσελίδας', 'Website deleted'],
  'tenant.deleted': ['Διαγραφή πελάτη', 'Client deleted'],
  'source.deleted': ['Διαγραφή πηγής', 'Source deleted'],
  'member.invited': ['Πρόσκληση μέλους', 'Member invited'],
  'member.role_changed': ['Αλλαγή ρόλου', 'Member role changed'],
  'member.removed': ['Αφαίρεση μέλους', 'Member removed'],
  'auth.invite.accepted': ['Αποδοχή πρόσκλησης', 'Invitation accepted'],
  'preview.session': ['Δοκιμή απαντήσεων', 'Answer preview'],
  'conversation.deleted': ['Διαγραφή συνομιλίας', 'Conversation deleted'],
  'lead.updated': ['Ενημέρωση ενδιαφέροντος', 'Lead updated'],
  'lead.deleted': ['Διαγραφή ενδιαφέροντος', 'Lead deleted'],
  'subscription.assigned': ['Ανάθεση πακέτου', 'Plan assigned'],
  'subscription.updated': ['Ενημέρωση πακέτου', 'Plan updated'],
  'platform.tenant_access': ['Προβολή υποστήριξης', 'Support access'],
};

function when(value: string, locale: 'el' | 'en'): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale === 'en' ? 'en-GB' : 'el-GR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

/** Tenant landing page. Numbers come from sites, usage, subscription and audit. */
export function OverviewPage(props: { canWrite: boolean }) {
  const { locale, text } = useAdminLocale();
  const sites = useRemote<SiteRow[]>('/v1/sites');
  const usage = useRemote<UsageRow>('/v1/usage');
  const subscription = useRemote<{ status: string; plan: { code: string; name: string } }>(
    '/v1/subscription',
  );
  const audit = useRemote<AuditRow[]>('/v1/audit');
  const rows = sites.data ?? [];
  const sitesPaging = useClientPaging(rows);
  const active = rows.filter((site) => site.status === 'ACTIVE').length;
  const quota = usage.data?.answers.quota ?? 0;
  const completed = usage.data?.answers.completed ?? 0;
  const meter = quota > 0 ? Math.round((completed / quota) * 100) : 0;
  const activity = (audit.data ?? []).slice(0, 6);

  return (
    <section className="admin-page">
      <PageHeader
        description={text(
          'Ιστοσελίδες, απαντήσεις και χρήση πακέτου του οργανισμού σας.',
          "Your organization's websites, answers, and plan usage.",
        )}
        actions={
          props.canWrite ? (
            <Link to="/sites" className={buttonClass('primary')}>
              <Icon name="plus" className="size-4" /> {text('Ιστοσελίδα', 'Website')}
            </Link>
          ) : null
        }
      />
      {sites.error ? <ErrorNote message={sites.error.message} /> : null}
      {usage.error ? <ErrorNote message={usage.error.message} /> : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          icon="globe"
          label={text('Ενεργές ιστοσελίδες', 'Active websites')}
          value={sites.data ? active : '—'}
          hint={
            sites.data
              ? text(`${rows.length} συνολικά`, `${rows.length} total`)
              : text('Φόρτωση ιστοσελίδων…', 'Loading websites…')
          }
        />
        <Stat
          icon="chat"
          label={text('Απαντήσεις περιόδου', 'Answers this period')}
          value={usage.data ? completed : '—'}
          hint={
            usage.data
              ? text(
                  `κρατημένες ${usage.data.answers.reserved}`,
                  `${usage.data.answers.reserved} reserved`,
                )
              : (usage.error?.message ?? text('Φόρτωση χρήσης…', 'Loading usage…'))
          }
        />
        <Stat
          icon="layers"
          label={text('Πακέτο', 'Plan')}
          value={subscription.data?.plan.name ?? '—'}
          hint={
            subscription.error?.message ??
            (subscription.data?.status === 'ACTIVE'
              ? text('Ενεργό', 'Active')
              : subscription.data?.status === 'SUSPENDED'
                ? text('Σε αναστολή', 'Suspended')
                : (subscription.data?.status ??
                  text('Φόρτωση συνδρομής…', 'Loading subscription…')))
          }
        />
        <Stat
          icon="chart"
          label={text('Χρήση πακέτου', 'Plan usage')}
          value={usage.data ? `${meter}%` : '—'}
          meter={usage.data ? meter : undefined}
          hint={
            usage.data
              ? text(
                  `${completed} / ${quota} απαντήσεις · ${usage.data.estimatedCost.amount} ${usage.data.estimatedCost.currency}`,
                  `${completed} / ${quota} answers · ${usage.data.estimatedCost.amount} ${usage.data.estimatedCost.currency}`,
                )
              : text(
                  'Η εκτίμηση κόστους δεν είναι τιμολόγιο',
                  'The cost estimate is not an invoice',
                )
          }
        />
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <Panel
          loading={!sites.data && !sites.error}
          error={sites.error?.message ?? null}
          empty={rows.length === 0}
          emptyLabel={text('Δεν υπάρχουν ιστοσελίδες.', 'No websites found.')}
          emptyHint={
            props.canWrite
              ? text(
                  'Δημιουργήστε την πρώτη ιστοσελίδα από τη σελίδα Ιστοσελίδες.',
                  'Create the first website from the Websites page.',
                )
              : text(
                  'Ο διαχειριστής του οργανισμού δημιουργεί ιστοσελίδες.',
                  'The organization administrator creates websites.',
                )
          }
        >
          <Card title={text('Ιστοσελίδες', 'Websites')} flush>
            <PagedTableShell pagination={sitesPaging.total > 0 ? sitesPaging.pager : null}>
              <Table head={[text('Ιστοσελίδα', 'Website'), text('Κατάσταση', 'Status'), '']}>
                {sitesPaging.rows.map((site) => (
                  <tr key={site.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                    <Td>
                      <div className="flex items-center gap-3">
                        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600">
                          <Icon name="globe" className="size-4" />
                        </span>
                        <div className="min-w-0">
                          <Link
                            className="font-medium text-blue-600 hover:text-blue-800 dark:hover:text-blue-200"
                            to={`/sites/${site.id}`}
                          >
                            {site.name}
                          </Link>
                          <p className="truncate text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                            {site.canonicalOrigin}
                          </p>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <Badge value={site.status} />
                    </Td>
                    <Td className="text-right">
                      <Link
                        to={`/sites/${site.id}`}
                        className="inline-flex text-slate-400 dark:text-slate-500 hover:text-blue-600"
                        aria-label={text(`Άνοιγμα ${site.name}`, `Open ${site.name}`)}
                      >
                        <Icon name="arrow" className="size-4" />
                      </Link>
                    </Td>
                  </tr>
                ))}
              </Table>
            </PagedTableShell>
          </Card>
        </Panel>
        <Panel
          loading={!audit.data && !audit.error}
          error={audit.error?.message ?? null}
          empty={activity.length === 0}
          emptyLabel={text('Δεν υπάρχει πρόσφατη δραστηριότητα.', 'No recent activity.')}
          emptyHint={text(
            'Οι ενέργειες διαχείρισης εμφανίζονται εδώ.',
            'Administrative actions appear here.',
          )}
        >
          <Card title={text('Πρόσφατη δραστηριότητα', 'Recent activity')}>
            <ol className="space-y-3">
              {activity.map((row) => {
                const icon = activityIcon(row.action);
                const label = AUDIT_LABEL[row.action];
                return (
                  <li key={row.id} className="flex items-start gap-3 text-sm">
                    <span
                      className={`grid size-8 shrink-0 place-items-center rounded-full ${icon.tone}`}
                    >
                      <Icon name={icon.name} className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1 text-slate-800 dark:text-slate-200">
                      {label ? text(label[0], label[1]) : row.action}
                    </span>
                    <time
                      className="shrink-0 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500"
                      dateTime={row.createdAt}
                    >
                      {when(row.createdAt, locale)}
                    </time>
                  </li>
                );
              })}
            </ol>
          </Card>
        </Panel>
      </div>
    </section>
  );
}
